import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Clock, Tag, Loader2, BookOpen } from 'lucide-react';
import { fetchPost } from '../lib/supabase';
import './BlogPost.css';

/* Very simple markdown renderer — handles the subset we use in posts */
function renderMarkdown(md) {
  if (!md) return [];
  const lines = md.split('\n');
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.startsWith('## ')) {
      elements.push(<h2 key={i} className="bp__h2">{line.slice(3)}</h2>);
    } else if (line.startsWith('# ')) {
      elements.push(<h1 key={i} className="bp__h1">{line.slice(2)}</h1>);
    } else if (line.startsWith('**') && line.endsWith('**')) {
      // standalone bold line — treat as subhead
      elements.push(<p key={i} className="bp__bold">{line.slice(2, -2)}</p>);
    } else if (line.startsWith('- ')) {
      // collect consecutive list items
      const items = [];
      while (i < lines.length && lines[i].startsWith('- ')) {
        items.push(lines[i].slice(2));
        i++;
      }
      elements.push(
        <ul key={`ul-${i}`} className="bp__list">
          {items.map((item, j) => (
            <li key={j} dangerouslySetInnerHTML={{ __html: inlineFormat(item) }} />
          ))}
        </ul>
      );
      continue;
    } else if (line.trim() === '') {
      // skip blank lines
    } else {
      elements.push(
        <p key={i} className="bp__p" dangerouslySetInnerHTML={{ __html: inlineFormat(line) }} />
      );
    }
    i++;
  }
  return elements;
}

/* Handle inline **bold** and *italic* */
function inlineFormat(text) {
  return text
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>');
}

const CAT_COLORS = {
  Guide: '#6d4fff',
  Analysis: '#00e5ff',
  Playbook: '#22d3a0',
  Roundup: '#f472b6',
  Comparison: '#fbbf24',
};

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchPost(slug)
      .then(setPost)
      .catch(() => setError('Article not found.'))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <main className="bp__page" style={{ paddingTop: 'var(--nav-h)' }}>
        <div className="bp__loading">
          <Loader2 size={28} className="tools-page__spinner" style={{ color: 'var(--text-accent)' }} />
          <p style={{ color: 'var(--text-muted)' }}>Loading article…</p>
        </div>
      </main>
    );
  }

  if (error || !post) {
    return (
      <main className="bp__page" style={{ paddingTop: 'var(--nav-h)' }}>
        <div className="bp__loading">
          <BookOpen size={32} style={{ color: 'var(--text-muted)' }} />
          <p style={{ color: 'var(--text-muted)' }}>Article not found.</p>
          <Link to="/blog" className="btn btn-ghost btn-sm">← Back to Blog</Link>
        </div>
      </main>
    );
  }

  const catColor = CAT_COLORS[post.category] || '#6d4fff';

  return (
    <main className="bp__page" style={{ paddingTop: 'var(--nav-h)' }}>
      <div className="bp__inner section-inner">

        {/* Back nav */}
        <Link to="/blog" className="bp__back">
          <ArrowLeft size={16} /> Back to Blog
        </Link>

        {/* Header */}
        <header className="bp__header">
          <div className="bp__meta-row">
            <span className="blog-cat-tag" style={{ '--cat-c': catColor }}>{post.category}</span>
            <span className="blog-meta"><Clock size={12} />{post.read_time} read</span>
            <span className="blog-meta">
              {new Date(post.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
          </div>

          <h1 className="bp__title">{post.title}</h1>
          <p className="bp__excerpt">{post.excerpt}</p>

          <div className="bp__author-row">
            <div className="blog__avatar">{post.author_avatar}</div>
            <div>
              <div className="bp__author-name">{post.author_name}</div>
              <div className="bp__author-role">ToolMind Writer</div>
            </div>
          </div>
        </header>

        <div className="divider" style={{ margin: 'var(--sp-8) 0' }} />

        {/* Article body */}
        <article className="bp__body">
          {renderMarkdown(post.content)}
        </article>

        <div className="divider" style={{ margin: 'var(--sp-10) 0' }} />

        {/* Tags */}
        {post.tags?.length > 0 && (
          <div className="bp__tags">
            {post.tags.map(t => (
              <span key={t} className="tag"><Tag size={10} />{t}</span>
            ))}
          </div>
        )}

        {/* Back link */}
        <div className="bp__footer">
          <Link to="/blog" className="btn btn-ghost">
            <ArrowLeft size={16} /> Back to all articles
          </Link>
        </div>

      </div>
    </main>
  );
}
